import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { getSubscriptions } from '@innovyze/stylovyze';
import { viewerAccess } from '@innovyze/shared-utils';
import { useGlobalization } from '@Translations/useGlobalization';
import { getRiskResultsSummary } from '@Actions';
import { useResultsRunState } from '@Hooks/useResultsRunState';
import { selectRiskRunning } from '@Selectors/util.selectors';
import { selectRiskSetting } from '@Selectors/Risk.selectors';
import {
	selectCompletedRun,
	selectResultsSummaryForConfig,
} from '@Selectors/RiskResultsSummary.selectors';
import { NamespacedStoreState } from '@Types/store.types';
import { GRADE_ORDER } from '@Components/RiskDetails/Datagrid/gradeConfig';
import { ResultsKpiBar } from './ResultsKpiBar.component';
import { ResultsEmptyState } from './ResultsEmptyState.component';

interface Props {
	configId: string;
	/** The existing results content (grid, or grid + map). */
	children: React.ReactNode;
}

// Flag-on Results tab: decides from run status (not row count) whether to show
// the empty state or the KPI bar above the existing results content.
export const ResultsTab = ({ configId, children }: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const runState = useResultsRunState(configId);
	const isRunning = selectRiskRunning();
	const { assetType } = selectRiskSetting();
	const completedRun = useSelector(selectCompletedRun);
	const view = useSelector((state: NamespacedStoreState) =>
		selectResultsSummaryForConfig(state, configId),
	);
	const canEdit = !viewerAccess(getSubscriptions());

	const fetchSummary = useCallback(
		() => dispatch(getRiskResultsSummary(configId)),
		[dispatch, configId],
	);

	// Fetch on open / model switch, and again whenever a run completes in
	// session (completedRun.count bumps on recordRunCompleted).
	useEffect(() => {
		if (runState === 'complete') fetchSummary();
	}, [fetchSummary, runState, completedRun.count]);

	if (runState === 'notRun') {
		return (
			<ResultsEmptyState
				isRunning={!!isRunning}
				canEdit={canEdit}
				configId={configId}
			/>
		);
	}

	// Run before, but no completed run on record: results may still exist, so
	// let the grid decide (rows, or its own empty state). No KPI cards — the
	// summary can't be trusted to describe a completed run here.
	if (runState === 'noCompletedRun') {
		return (
			<>
				{isRunning && (
					<ResultsEmptyState
						isRunning
						canEdit={canEdit}
						configId={configId}
					/>
				)}
				{children}
			</>
		);
	}

	let kpi: React.ReactNode;
	if (runState === 'complete' && view.data) {
		kpi = (
			<ResultsKpiBar
				key={configId}
				summary={view.data}
				assetType={assetType}
			/>
		);
	} else if (view.status === 'error') {
		kpi = (
			<Box
				data-cy="risk-results-kpi-error"
				sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
				<Typography variant="body2">
					{t('Could not load the results summary')}
				</Typography>
				<Button size="small" onClick={fetchSummary}>
					{t('Retry')}
				</Button>
			</Box>
		);
	} else {
		kpi = (
			<Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
				{/* One placeholder per grade card, keyed by grade. */}
				{GRADE_ORDER.map(grade => (
					<Skeleton
						key={grade}
						data-testid="risk-results-kpi-skeleton"
						variant="rectangular"
						height={120}
						sx={{ flex: 1, borderRadius: 1 }}
					/>
				))}
			</Box>
		);
	}

	return (
		<>
			{kpi}
			{children}
		</>
	);
};

export default ResultsTab;
